import {
	IDrawerDataListColumn,
	Id,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { eventService } from "../../../shared/api/eventService";
import { userTenantService } from "../../../shared/api/userTenantService";
import { PermissionName } from "../../../shared/types/permission";
import { TenantIndexResource } from "../../../shared/types/tenant";
import { clippingsService } from "../shared/api/clippingsService";
import { companyService } from "../shared/api/companyService";
import { contactService } from "../shared/api/contactService";
import { distributionListService } from "../shared/api/distributionListService";
import { tenantService } from "../shared/api/tenantService";
import { TenantFormValues, TenantSaveRequest } from "../shared/types";
import { TenantContextMenu } from "../tenants/TenantContextMenu";
import { TenantCreateDrawer, TenantUpdateDrawer } from "./TenantDrawer";

const mapToRequest = ({ users, ...tenant }: TenantFormValues): TenantSaveRequest => ({
	...tenant,
	userIds: users.map((x) => x.id),
});

export const TenantsPage = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const canCreate = useHasPermission<PermissionName>(["tenant.create"]);
	const canUpdate = useHasPermission<PermissionName>(["tenant.update"]);
	const crudOperations = useLaravelCrudEntity(
		tenantService,
		{
			entityName: t("Tenant"),
			messages: {
				create: t("Tenant was saved."),
				update: t("Tenant was saved."),
				delete: t("Tenant was deleted."),
			},
		},
		[tenantService.selectedKey, userTenantService.allOptionsKey, contactService.indexKey, companyService.indexKey],
	);

	const updateTenant = useCallback(
		async (tenantId: Id, entityFormState: TenantFormValues) => {
			const response = await crudOperations.updateEntity(tenantId, mapToRequest(entityFormState));
			if (response) {
				void queryClient.resetQueries({ queryKey: clippingsService.indexKey });
				void queryClient.resetQueries({ queryKey: distributionListService.indexKey });
				void queryClient.resetQueries({ queryKey: eventService.indexKey });
			}
			return response;
		},
		[crudOperations, queryClient],
	);

	const dataListColumns: IDrawerDataListColumn<TenantIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<TenantContextMenu id={id} onEdit={openDrawer} onDelete={crudOperations.deleteEntity} />
				),
			},
		],
		[t, crudOperations.deleteEntity],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(tenantService.listKey, tenantService.index)}
			renderCreateDrawer={(drawerProps) => (
				<TenantCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(mapToRequest(entityFormState))}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<TenantUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => updateTenant(drawerProps.id, entityFormState)}
				/>
			)}
			translations={{
				title: t("Tenants"),
				empty: t("No tenant available."),
			}}
		/>
	);
};
